import { useEffect } from 'react';
import Button from '@cloudscape-design/components/button';
import { toast } from '@/lib/notifications';

// Evento BeforeInstallPrompt — não está nos lib.dom.d.ts ainda em alguns
// targets, declaramos minimal aqui pra evitar `any`.
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

type Props = {
  title: string;
  description: string;
  actionLabel: string;
  /** Chave única por app pra não repetir o prompt na mesma sessão. */
  storageKey: string;
};

const TOAST_ID = 'install-prompt';

// 2ª auditoria (2026-10-25 21:32 +0100, #1021): `sessionStorage` lança SecurityError com o
// armazenamento do site bloqueado (Safari privado restrito, política corporativa). Sem o try, o
// efeito estourava fora de qualquer ErrorBoundary útil e o handler do X derrubava a recusa — a
// preferência é conveniência, então falhar vira "não lembrado", nunca erro na tela.
// A cópia em memória é o que segura a recusa na página quando o armazenamento está bloqueado.
const memory = new Map<string, string>();

function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key) ?? memory.get(key) ?? null;
  } catch {
    return memory.get(key) ?? null;
  }
}

function writeSession(key: string, value: string): void {
  memory.set(key, value);
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // armazenamento bloqueado: a recusa vale só enquanto a página estiver aberta
  }
}

/**
 * Escuta `beforeinstallprompt` (Chrome/Edge/Brave) e oferece uma notificação
 * persistente (Flashbar via `@/lib/notifications`) com ação "Instalar". Após o
 * usuário clicar, marca em sessionStorage pra não reaparecer na mesma sessão.
 *
 * Portado de `@etiquetachef/ui-shared` para zerar a dependência shadcn no admin
 * (Sprint 39): o `toast` do Sonner virou o store Flashbar do admin, e a ação
 * virou um `<Button>` Cloudscape. iOS Safari não emite o evento — ignoramos.
 *
 * Renderiza `null` — toda a UI vai pela notificação.
 *
 * Origem: sem issue — commit 5ee344f94, 2026-06-09 (bane `ui-shared` no admin).
 *
 * Usado por: `main.tsx` (`InstallPromptI18n`, com `storageKey`
 * `etiquetachef:admin:install-prompt`), fora do router.
 *
 * Dicas: `sessionStorage` e não `localStorage` de propósito — recusar o
 * prompt vale só para a sessão; na próxima o navegador volta a oferecer. Todo acesso passa
 * por `readSession`/`writeSession`, que não lançam com o armazenamento bloqueado.
 *
 * Histórico:
 *  - 2026-10-25 15:00 +0100 — (#1021) fechar pelo X grava a recusa.
 *  - 2026-10-25 21:32 +0100 — (#1021, 2ª auditoria) armazenamento bloqueado não lança; evento
 *    reemitido depois do X não traz o aviso de volta.
 */
export function InstallPrompt({ title, description, actionLabel, storageKey }: Props): null {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (readSession(storageKey) === 'dismissed') return;

    let deferred: BeforeInstallPromptEvent | null = null;

    const onBeforeInstall = (event: Event): void => {
      event.preventDefault();
      // Recusado pelo X nesta página: o evento pode voltar sem recarga (#1021, 2ª auditoria).
      if (readSession(storageKey) === 'dismissed') return;
      deferred = event as BeforeInstallPromptEvent;
      toast.message(description, {
        id: TOAST_ID,
        header: title,
        duration: Infinity,
        // Bug corrigido em 2026-10-25 15:00 +0100 (#1021): fechar pelo X não gravava a recusa, e o
        // aviso (sem prazo, e fixo no topo desde o `stickyNotifications`) voltava a cada carga.
        onDismiss: (): void => {
          writeSession(storageKey, 'dismissed');
        },
        action: (
          <Button
            onClick={(): void => {
              if (!deferred) return;
              void (async (): Promise<void> => {
                await deferred.prompt();
                const choice = await deferred.userChoice;
                writeSession(storageKey, choice.outcome);
                deferred = null;
                toast.dismiss(TOAST_ID);
              })();
            }}
          >
            {actionLabel}
          </Button>
        ),
      });
    };

    const onInstalled = (): void => {
      writeSession(storageKey, 'accepted');
      toast.dismiss(TOAST_ID);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return (): void => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [actionLabel, description, storageKey, title]);

  return null;
}
